/** Shared world geometry + engine-facing tuning (the Phaser side). */
export const WORLD = {
  width: 1024,
  height: 768,
  /** Arena (dish) geometry in world pixels. */
  arena: {
    cx: 512,
    cy: 412,
    /** Rim radius — the lip of the dish. */
    rim: 300,
    /** Visual outer ring (decorative). */
    outer: 332,
    /** Dish art display diameter as a multiple of rim (art floor ≈ rim). */
    dishScale: 2.85,
  },
  beyRadius: 26,
  /**
   * Fraction of the bey art's half-width occupied by the visible metal ring.
   * The physics collision circle is sized to this so edges bounce on contact
   * instead of overlapping.
   */
  ringFill: 0.92,
} as const;

/**
 * Engine-side movement tuning, in "Matter velocity" units (≈ px per 1/60s).
 * Distinct from the pure SIM tuning in game/config.ts.
 */
export const PHYS = {
  /** Steering acceleration per frame at speed stat = 10. */
  steerAccel: 0.6,
  /** Hard cap on a bey's speed. */
  maxSpeed: 9.5,
  /**
   * Per-frame velocity retention (drag). Kept near-frictionless so beys keep
   * their orbital momentum and swoop around the bowl for a while.
   */
  drag: 0.994,
  /** Arena center-pull strength (accel per unit distance / 1000). */
  pull: 0.00045,
  /**
   * Tangential speed each bey is launched with at the start of a round, so the
   * center-pull turns into a wide orbital swoop rather than a dive to center.
   */
  launchSpeed: 5.0,
  /** Extra center-pull multiplier while at center stage. */
  holdPullMult: 5,
  /** Extra drag at center stage (anchors the bey in the middle). */
  holdDrag: 0.86,
  /** Below this speed (and near center) a bey settles into sticky center stage. */
  centerStageSpeed: 2.2,

  /** Strike/dash — a big committed lunge. */
  dash: {
    impulse: 9.5,
    boostCap: 12.5, // speed cap briefly raised so the lunge really flings
    boostTime: 0.45, // seconds the raised cap lasts
    cooldown: 1.5, // seconds
    spinCost: 3,
  },
  /** Extreme dash (ride the rail, then slingshot back) — the big spectacle. */
  extreme: {
    outForce: 1.05, // per-frame push toward the rail
    railTime: 1.1, // seconds spent riding around the rim before striking
    railSpeed: 8.5, // tangential speed while riding the rail
    slingSpeed: 15, // speed when launched back at the opponent
    cooldown: 5, // a touch longer since the move now lasts longer
    spinCost: 9,
    boostTime: 0.55, // seconds the bonus-impact window lasts after launch
    impactBonus: 2.4, // impact multiplier during the boost window
  },

  /** Special-move physics. */
  special: {
    /** Homing super-dash speed for the attack special (Blazing Rush). */
    rushSpeed: 14.5,
    /** Impact multiplier while a rush is active. */
    rushImpactBonus: 2.8,
    /** Steering boost while the balance special (Tailwind) is active. */
    focusSteerMult: 1.7,
    /** Idle-drain multiplier while Tailwind is active. */
    focusDrainMult: 0.4,
    /** Small instant spin recovery from Tailwind. */
    focusHeal: 10,
  },

  /** How much computed clash knockback is scaled before applying. */
  knockbackScale: 0.2,
  /**
   * Ring-out rules. A bey only flies out if it was recently *knocked* by a
   * clash it lost AND crosses the rim faster than `speed`. A bey's own
   * momentum (swooping, dashing, an extreme dash) just wall-rides and bounces
   * back — so an aggressor never rings itself out.
   */
  ringOut: {
    /** Outward radial speed above which a knocked bey flies out. */
    speed: 7.6,
    /** Seconds a clash keeps a bey "knocked" and thus ring-out-able. */
    hitWindow: 0.5,
    /** Minimum clash impact that counts as a real knock (not a light bump). */
    minImpact: 5,
  },
  /** Velocity kept after a wall bounce. */
  bounceRestitution: 0.55,
} as const;
